import * as type from './types';
import { CHAT_API_BASE, chatApi, paneContentModes, notificationTypes } from '../../config/constants';
import { updateSlatePane, openTemplateInPane, closeSlatePane } from './slatePane';
import { showNotification } from './notification';
import { createSSEParser } from '../../helpers/sseParser';
import { logout } from './user';
import i18next from 'i18next';
import isEqual from 'lodash/isEqual';

var SESSION_KEY = 'slateMateChatSessionUuid';
var MESSAGES_KEY = 'slateMateChatMessages';
var ATTACHMENTS_KEY = 'slateMateChatAttachments';

export var CHAT_TIMING = {
  IDLE_MS: 90000,
  ABSOLUTE_MS: 600000,
  POLL_MS: 5000,
  RECOVERY_MAX_POLLS: 60,
  TAKING_A_WHILE_MS: 20000,
  GET_TIMEOUT_MS: 15000,
};

// The turn (live stream or recovery poll) the module is running, or null.
// Shape: { controller, userCancelled, sessionGone, recovering, sleepTimer, sleepResolve }.
var activeTurn = null;

// Bumped whenever a turn starts, is cancelled by the user or is torn down. A mount reconcile GET captures it
// before fetching and drops its response when it changed, so a late reply cannot restart a cancelled recovery.
var turnGeneration = 0;

// JSON string of the last template this client displayed (spec decision 9).
var lastShownTranslation = null;

function newTurn() {
  turnGeneration++;
  return {
    controller: null,
    userCancelled: false,
    sessionGone: false,
    recovering: false,
    sleepTimer: null,
    sleepResolve: null,
  };
}

// Abort whatever the turn is waiting on (a GET in flight, the poll sleep).
function wakeTurn(turn) {
  if (turn.controller) {
    try { turn.controller.abort(); } catch (e) { /* ignore */ }
  }
  if (turn.sleepTimer) {
    clearTimeout(turn.sleepTimer);
    turn.sleepTimer = null;
  }
  if (turn.sleepResolve) {
    var resolve = turn.sleepResolve;
    turn.sleepResolve = null;
    resolve();
  }
}

// The chat session this turn serves is gone (Start Over, logout, account switch):
// stop the turn at once, dispatch nothing.
export function cancelActiveTurn() {
  var turn = activeTurn;
  activeTurn = null;
  turnGeneration++;
  lastShownTranslation = null;
  if (turn) {
    turn.sessionGone = true;
    wakeTurn(turn);
  }
}

function persistSessionUuid(id) {
  try {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  } catch (e) { /* ignore */ }
}

function persistMessages(messages) {
  try {
    sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
  } catch (e) { /* ignore */ }
}

function persistAttachments(attachments) {
  try {
    sessionStorage.setItem(ATTACHMENTS_KEY, JSON.stringify(attachments));
  } catch (e) { /* ignore */ }
}

function readSession() {
  try {
    var id = sessionStorage.getItem(SESSION_KEY);
    var messagesRaw = sessionStorage.getItem(MESSAGES_KEY);
    var attachmentsRaw = sessionStorage.getItem(ATTACHMENTS_KEY);
    return {
      sessionUuid: id || null,
      messages: messagesRaw ? JSON.parse(messagesRaw) : [],
      pendingAttachments: attachmentsRaw ? JSON.parse(attachmentsRaw) : [],
    };
  } catch (e) {
    return { sessionUuid: null, messages: [], pendingAttachments: [] };
  }
}

export function clearChatSessionStorage() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(MESSAGES_KEY);
    sessionStorage.removeItem(ATTACHMENTS_KEY);
  } catch (e) { /* ignore */ }
}

export function hydrateChatSession() {
  return function (dispatch, getState) {
    var hydrated = readSession();
    var payload = Object.assign({}, hydrated);
    if (activeTurn) {
      // A running turn lives in module state and owns the Redux session and transcript (its live assistant
      // bubble is newer than the storage snapshot), so keep them and only keep the guards (Send, Retry,
      // Start Over) on after a remount.
      var live = getState().chatSession;
      payload = Object.assign({}, live, {
        streamStatus: 'streaming',
        recovering: activeTurn.recovering === true,
      });
      hydrated = { sessionUuid: live.sessionUuid || hydrated.sessionUuid };
    }
    dispatch({ type: type.CHAT_SESSION_HYDRATE, payload: payload });
    return hydrated.sessionUuid;
  };
}

export function createSession() {
  return function (dispatch) {
    return fetch(CHAT_API_BASE + chatApi.CREATE_SESSION, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('createSession failed: ' + res.status);
      return res.json();
    }).then(function (data) {
      persistSessionUuid(data.sessionUuid);
      dispatch({ type: type.CHAT_SESSION_SET_UUID, payload: { sessionUuid: data.sessionUuid } });
      return data.sessionUuid;
    });
  };
}

function mapServerMessages(data) {
  return (data.messages || []).map(function (m) {
    return {
      uuid: m.uuid,
      role: m.role,
      text: m.text,
      createdAt: m.createdAt,
      status: 'complete',
      attachments: [],
    };
  });
}

export function showTranslationInPane(translation) {
  return function (dispatch, getState) {
    lastShownTranslation = JSON.stringify(translation);
    dispatch(updateSlatePane({ contentModified: false }));
    var paneOpen = getState().slatePane.isPaneOpen;
    if (paneOpen) {
      dispatch(updateSlatePane({
        contentJson: translation,
        contentMode: paneContentModes.TEMPLATE,
        contentModified: false,
      }));
    } else {
      dispatch(openTemplateInPane(translation));
    }
  };
}

// Recovery only: show a translation the client has not displayed yet, never over a regular slate.
function shouldShowRecovered(translation, slatePane) {
  var json = JSON.stringify(translation);
  if (slatePane.isPaneOpen) {
    if (!slatePane.contentJson) return false;
    if (isEqual(slatePane.contentJson, translation)) {
      lastShownTranslation = json;
      return false;
    }
  }
  return json !== lastShownTranslation;
}

function applySessionData(dispatch, getState, data, options) {
  var messages = mapServerMessages(data);
  persistMessages(messages);
  dispatch({
    type: type.CHAT_SESSION_RECONCILED,
    payload: {
      messages: messages,
      serverDocumentCount: (data.documents || []).length,
    },
  });
  if (!data.translation) return;
  var slatePane = getState().slatePane;
  if (options && options.replacePane) {
    if (shouldShowRecovered(data.translation, slatePane)) {
      dispatch(showTranslationInPane(data.translation));
    }
  } else if (!slatePane.isPaneOpen) {
    lastShownTranslation = JSON.stringify(data.translation);
    dispatch(openTemplateInPane(data.translation));
  }
}

export function reconcileSession(sessionUuid) {
  return function (dispatch, getState) {
    var generation = turnGeneration;
    // A turn started, was cancelled or the session changed while this GET was in flight: the reply is stale.
    function stale() {
      return generation !== turnGeneration || getState().chatSession.sessionUuid !== sessionUuid;
    }
    return fetch(CHAT_API_BASE + chatApi.GET_SESSION(sessionUuid), {
      method: 'GET',
      credentials: 'include',
    }).then(function (res) {
      if (res.status === 404) {
        if (!stale()) dispatch(clearSession({ silent: false, reason: 'expired' }));
        return null;
      }
      if (!res.ok) throw new Error('reconcileSession failed: ' + res.status);
      return res.json();
    }).then(function (data) {
      if (!data) return null;
      // A running turn owns messages and stream state; this GET must not touch them.
      if (activeTurn || stale()) return data;
      applySessionData(dispatch, getState, data, { replacePane: false });
      var follow = data.status === 'working'
        && getState().chatSession.streamStatus !== 'streaming'
        && getState().chatSession.sessionUuid === sessionUuid;
      if (!follow) return data;
      return dispatch(recoverTurn(sessionUuid, {
        failCode: 'network',
        sentText: null,
        ownsStream: true,
      })).then(function () { return data; });
    });
  };
}

function normalizeSentText(text) {
  return String(text).replace(/\r\n/g, '\n').trim();
}

// True when the server's last user message is the one this send posted (spec decision 7).
function serverHoldsTurn(data, sentText, knownUserUuids) {
  var messages = data.messages || [];
  for (var i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== 'user') continue;
    if ((knownUserUuids || []).indexOf(messages[i].uuid) !== -1) return false;
    return normalizeSentText(messages[i].text) === normalizeSentText(sentText);
  }
  return false;
}

// Find a lost turn's result by polling the session GET until the server is idle (spec "recoverTurn").
export function recoverTurn(sessionUuid, options) {
  return function (dispatch, getState) {
    options = options || {};
    var failCode = options.failCode;
    var turn = options.turn;
    if (!turn) {
      turn = newTurn();
      activeTurn = turn;
    }

    // 'silent': superseded or session gone, touch nothing. 'cancel': the user pressed Cancel.
    function stopReason() {
      if (activeTurn !== turn || turn.sessionGone || getState().chatSession.sessionUuid !== sessionUuid) {
        return 'silent';
      }
      if (turn.userCancelled) return 'cancel';
      return null;
    }

    function sleep(ms) {
      return new Promise(function (resolve) {
        turn.sleepResolve = resolve;
        turn.sleepTimer = setTimeout(function () {
          turn.sleepTimer = null;
          turn.sleepResolve = null;
          resolve();
        }, ms);
      });
    }

    // One session GET. Resolves { status, data } or { miss: true } (rejected, timed out, bad body).
    function getOnce() {
      var controller = new AbortController();
      turn.controller = controller;
      var timer = setTimeout(function () {
        try { controller.abort(); } catch (e) { /* ignore */ }
      }, CHAT_TIMING.GET_TIMEOUT_MS);
      return fetch(CHAT_API_BASE + chatApi.GET_SESSION(sessionUuid), {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      }).then(function (res) {
        if (!res.ok) return { status: res.status };
        return res.json().then(function (data) { return { status: res.status, data: data }; });
      }).catch(function () {
        return { miss: true };
      }).then(function (result) {
        clearTimeout(timer);
        if (turn.controller === controller) turn.controller = null;
        return result;
      });
    }

    function errored(code) {
      dispatch({
        type: type.CHAT_MESSAGE_ERRORED,
        payload: { code: code, message: 'recovery_' + code },
      });
    }

    // Step 1: settle the streaming bubble so Redux and the live text agree before polling.
    if (options.assistantUuid) {
      var bubble = null;
      var current = getState().chatSession.messages;
      for (var bi = current.length - 1; bi >= 0; bi--) {
        if (current[bi].uuid === options.assistantUuid) { bubble = current[bi]; break; }
      }
      if (bubble && bubble.status === 'streaming') {
        if (options.accumulated) {
          dispatch({
            type: type.CHAT_MESSAGE_APPEND_DELTA,
            payload: { text: options.accumulated, uuid: options.assistantUuid },
          });
        }
        emitStreaming({ type: 'paragraph_end', uuid: options.assistantUuid });
        emitStreaming({ type: 'stream_end', uuid: options.assistantUuid });
        dispatch({ type: type.CHAT_MESSAGE_END, payload: { uuid: options.assistantUuid } });
      }
    }
    if (options.ownsStream) dispatch({ type: type.CHAT_STREAM_STARTED });
    dispatch({ type: type.CHAT_RECOVERY_STARTED });
    turn.recovering = true;

    function finish(reason) {
      if (reason === 'silent') {
        if (activeTurn === turn) activeTurn = null;
        turn.recovering = false;
        return { silent: true };
      }
      if (reason === 'cancel') {
        dispatch({ type: type.CHAT_MESSAGE_INTERRUPTED, payload: {} });
      }
      dispatch({ type: type.CHAT_RECOVERY_ENDED });
      if (options.ownsStream) {
        dispatch({ type: type.CHAT_STREAM_ENDED, payload: {} });
        persistMessages(getState().chatSession.messages);
      }
      if (activeTurn === turn) activeTurn = null;
      turn.recovering = false;
      return { silent: false };
    }

    async function run() {
      var polls = 0;
      var rechecked = false;
      var max = CHAT_TIMING.RECOVERY_MAX_POLLS;
      while (true) {
        var stop = stopReason();
        if (stop) return finish(stop);
        polls++;
        var result = await getOnce();
        stop = stopReason();
        if (stop) return finish(stop);

        var wait = false;
        if (result.miss || result.status >= 500) {
          if (polls >= max) { errored(failCode); return finish(null); }
          wait = true;
        } else if (result.status === 404) {
          dispatch(clearSession({ silent: false, reason: 'expired' }));
          return finish('silent');
        } else if (result.status === 401) {
          dispatch(logout());
          return finish('silent');
        } else if (!result.data) {
          errored(failCode);
          return finish(null);
        } else if (result.data.status === 'working') {
          if (polls >= max) { errored('still_working'); return finish(null); }
          wait = true;
        } else {
          var data = result.data;
          if (typeof options.sentText === 'string' && !serverHoldsTurn(data, options.sentText, options.knownUserUuids)) {
            if (rechecked || polls >= max) { errored(failCode); return finish(null); }
            rechecked = true;
            wait = true;
          } else {
            applySessionData(dispatch, getState, data, { replacePane: true });
            var adopted = data.messages || [];
            if (adopted.length > 0 && adopted[adopted.length - 1].role === 'user') errored(failCode);
            return finish(null);
          }
        }
        if (wait) await sleep(CHAT_TIMING.POLL_MS);
      }
    }

    return run().catch(function () {
      // Never rejects; an unexpected throw settles the turn like a failed poll.
      if (stopReason() === null) errored(failCode);
      return finish(stopReason());
    });
  };
}

export function clearSession(options) {
  options = options || {};
  return function (dispatch, getState) {
    var sessionUuid = getState().chatSession.sessionUuid;
    cancelActiveTurn();
    if (sessionUuid && !options.skipServerDelete) {
      // Best-effort; ignore failures.
      fetch(CHAT_API_BASE + chatApi.DELETE_SESSION(sessionUuid), {
        method: 'DELETE',
        credentials: 'include',
      }).catch(function () { /* ignore */ });
    }
    clearChatSessionStorage();
    dispatch({ type: type.CHAT_SESSION_CLEARED });
    dispatch(closeSlatePane());
    if (!options.silent && options.reason === 'expired') {
      dispatch(showNotification(
        notificationTypes.INFO,
        i18next.t('slateGenerator:chatSessionExpired')
      ));
    }
  };
}

export function addPendingAttachment(attachment) {
  return function (dispatch, getState) {
    var current = getState().chatSession.pendingAttachments;
    if (current.length >= 3) return false;
    dispatch({ type: type.CHAT_PENDING_ATTACHMENT_ADDED, payload: { attachment: attachment } });
    persistAttachments(getState().chatSession.pendingAttachments);
    return true;
  };
}

export function removePendingAttachment(index) {
  return function (dispatch, getState) {
    dispatch({ type: type.CHAT_PENDING_ATTACHMENT_REMOVED, payload: { index: index } });
    persistAttachments(getState().chatSession.pendingAttachments);
  };
}

export function clearPendingAttachments() {
  return function (dispatch) {
    dispatch({ type: type.CHAT_PENDING_ATTACHMENTS_CLEARED });
    persistAttachments([]);
  };
}

// internal helpers exported for use by sendMessage thunk
export var _persistMessages = persistMessages;
export var _persistSessionUuid = persistSessionUuid;
export var _persistAttachments = persistAttachments;

// Streaming-text emitter — keeps per-token churn out of Redux.
// Subscribers (e.g. SlateMate component) receive:
//   { type: 'delta', uuid, text }
//   { type: 'paragraph_end', uuid }
//   { type: 'stream_end', uuid }
var streamingListeners = [];
export function subscribeStreamingDelta(listener) {
  streamingListeners.push(listener);
  return function () {
    var idx = streamingListeners.indexOf(listener);
    if (idx !== -1) streamingListeners.splice(idx, 1);
  };
}
function emitStreaming(payload) {
  for (var i = 0; i < streamingListeners.length; i++) {
    try { streamingListeners[i](payload); } catch (e) { /* ignore */ }
  }
}

export function abortActiveStream() {
  if (activeTurn) {
    turnGeneration++;
    activeTurn.userCancelled = true;
    wakeTurn(activeTurn);
  }
}

export function sendMessage(text, attachments) {
  return function (dispatch, getState) {
    var state = getState();
    var sessionUuid = state.chatSession.sessionUuid;
    var knownUserUuids = state.chatSession.messages
      .filter(function (m) { return m.role === 'user' && m.uuid; })
      .map(function (m) { return m.uuid; });

    // This send's turn. Registered at once so a Cancel or a newer send reaches it, and so an old turn that
    // resumes later sees it is no longer the active one.
    var turn = newTurn();
    activeTurn = turn;
    var sid = sessionUuid;

    function ensureSession() {
      if (sessionUuid) return Promise.resolve(sessionUuid);
      return dispatch(createSession()).then(function (id) { return id; });
    }

    // True while this send's turn is still the one that owns the chat state.
    function isCurrent() {
      return activeTurn === turn && !turn.sessionGone && getState().chatSession.sessionUuid === sid;
    }

    var paragraphAccumulator = '';
    var currentAssistantUuid = { value: null };
    var attachmentsCount = (attachments || []).length;

    var userMessage = {
      role: 'user',
      text: text,
      createdAt: new Date().toISOString(),
      status: 'complete',
      attachments: (attachments || []).map(function (a) {
        return { fileName: a.fileName, mimeType: a.mimeType };
      }),
    };

    dispatch({ type: type.CHAT_MESSAGE_QUEUED, payload: { message: userMessage } });
    _persistMessages(getState().chatSession.messages);
    dispatch(clearPendingAttachments());

    var recoveryResult = null;

    return ensureSession().then(function (id) {
      sid = id;
      var contentModified = state.slatePane.contentModified;
      var body = {
        message: text,
        attachments: attachments || [],
      };
      if (contentModified) {
        body.updatedSlate = state.slatePane.contentJson;
      }
      // [SM-DEBUG] outgoing request summary
      var _serialized = JSON.stringify(body);
      console.log('[SM-DEBUG] sendMessage body', {
        url: CHAT_API_BASE + chatApi.SEND_MESSAGE(sid),
        messageLength: text ? text.length : 0,
        attachmentsCount: attachmentsCount,
        attachmentMeta: (attachments || []).map(function (a) {
          return {
            fileName: a.fileName,
            mimeType: a.mimeType,
            contentLength: a.content ? a.content.length : 0,
          };
        }),
        totalBodyBytes: _serialized.length,
      });

      var controller = new AbortController();
      turn.controller = controller;
      dispatch({ type: type.CHAT_STREAM_STARTED });

      // Timers: the idle timer restarts on every parsed SSE event, the cap timer never restarts.
      // Either one firing marks a deadline abort; the abort is then handled as a lost stream.
      var deadlineHit = false;
      var settled = false;
      var takingAWhileTimer = null;
      var idleTimer = null;
      var capTimer = null;
      function onDeadline() {
        deadlineHit = true;
        try { controller.abort(); } catch (e) { /* ignore */ }
      }
      function startTakingAWhileTimer() {
        takingAWhileTimer = setTimeout(function () {
          dispatch({ type: type.CHAT_STREAM_TAKING_A_WHILE });
        }, CHAT_TIMING.TAKING_A_WHILE_MS);
      }
      function restartTakingAWhileTimer() {
        if (settled) return;
        clearTimeout(takingAWhileTimer);
        startTakingAWhileTimer();
      }
      function restartIdleTimer() {
        if (settled) return;
        clearTimeout(idleTimer);
        idleTimer = setTimeout(onDeadline, CHAT_TIMING.IDLE_MS);
      }
      // One settle function for every exit path (clean end, error event, HTTP error, Cancel, deadline, a throw).
      function settle() {
        settled = true;
        clearTimeout(takingAWhileTimer);
        clearTimeout(idleTimer);
        clearTimeout(capTimer);
      }
      startTakingAWhileTimer();
      restartIdleTimer();
      capTimer = setTimeout(onDeadline, CHAT_TIMING.ABSOLUTE_MS);

      var recoverFailCode = null;

      return fetch(CHAT_API_BASE + chatApi.SEND_MESSAGE(sid), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      }).then(function (res) {
        // [SM-DEBUG] response received
        console.log('[SM-DEBUG] sendMessage response', {
          status: res.status,
          ok: res.ok,
          contentType: res.headers && res.headers.get ? res.headers.get('content-type') : null,
        });
        if (res.status === 404) {
          dispatch(clearSession({ silent: false, reason: 'expired' }));
          throw new Error('session_evicted');
        }
        if (!res.ok) {
          return res.json().catch(function () { return null; }).then(function (errBody) {
            var err = new Error('http_' + res.status);
            err.status = res.status;
            err.body = errBody;
            throw err;
          });
        }
        return readSSE(res, dispatch, getState, function (committed) {
          paragraphAccumulator = committed;
        }, function () { return paragraphAccumulator; }, controller, restartTakingAWhileTimer,
        restartIdleTimer, currentAssistantUuid).then(function (result) {
          if (!result.endedCleanly) {
            // The stream closed without a done or error event: the server may still hold the turn.
            recoverFailCode = 'network';
            return;
          }
          // Success path: bump server doc count for any attachments sent this turn.
          if (attachmentsCount > 0) {
            dispatch({
              type: type.CHAT_SERVER_DOCUMENT_COUNT_INCREMENTED,
              payload: { delta: attachmentsCount },
            });
          }
        });
      }).catch(function (err) {
        try {
          // [SM-DEBUG] send error details
          console.log('[SM-DEBUG] sendMessage ERROR', {
            name: err && err.name,
            message: err && err.message,
            status: err && err.status,
            body: err && err.body,
          });
          if (turn.userCancelled) {
            // Only the current turn may mark a bubble interrupted; a newer turn owns the bubbles now.
            if (isCurrent()) dispatch({ type: type.CHAT_MESSAGE_INTERRUPTED, payload: {} });
          } else if (turn.sessionGone) {
            // Session cleared (Start Over, logout, account switch): nothing to show.
          } else if (deadlineHit) {
            recoverFailCode = 'timeout';
          } else if (err.message === 'session_evicted') {
            // Already handled by clearSession
          } else if (err.status === 401) {
            dispatch(logout());
          } else if (!isCurrent()) {
            // A newer turn owns the bubbles; do not write an error onto them.
          } else if (err.status === 409 && err.body && err.body.error === 'session_busy') {
            dispatch({
              type: type.CHAT_MESSAGE_ERRORED,
              payload: { code: 'session_busy', message: err.body.error },
            });
          } else if (err.status === 400 && err.body && err.body.error === 'document_cap_exceeded') {
            dispatch({
              type: type.CHAT_MESSAGE_ERRORED,
              payload: { code: 'document_cap_exceeded', message: err.body.error },
            });
          } else if (err.status === 400 && err.body && err.body.code === 'invalid_slate') {
            // Refetch and surface
            dispatch(reconcileSession(sid));
            dispatch({
              type: type.CHAT_MESSAGE_ERRORED,
              payload: { code: 'invalid_slate', message: err.body.message },
            });
          } else if (err.status === 400 && err.body && err.body.errors && err.body.errors.length) {
            dispatch({
              type: type.CHAT_MESSAGE_ERRORED,
              payload: { code: 'validation', message: err.body.errors[0].message },
            });
          } else if (err.status) {
            dispatch({
              type: type.CHAT_MESSAGE_ERRORED,
              payload: { code: 'network', message: err.message },
            });
          } else {
            // Rejected without an HTTP status (offline, body read failed, parser threw): the server may still hold the turn.
            recoverFailCode = 'network';
          }
        } catch (e) {
          recoverFailCode = 'network';
        }
      }).then(function () {
        settle();
        if (!recoverFailCode) return null;
        return dispatch(recoverTurn(sid, {
          failCode: recoverFailCode,
          sentText: text,
          ownsStream: false,
          assistantUuid: currentAssistantUuid.value,
          accumulated: paragraphAccumulator,
          knownUserUuids: knownUserUuids,
          turn: turn,
        }));
      }).then(function (result) {
        recoveryResult = result;
      });
    }, function () {
      // createSession failed: show the network error on a stub assistant bubble.
      if (isCurrent()) {
        dispatch({
          type: type.CHAT_MESSAGE_ERRORED,
          payload: { code: 'network', message: 'createSession failed' },
        });
      }
    }).then(function () {
      // Ending step. Runs only for a turn that still owns the chat (spec decision 12).
      var sameSession = getState().chatSession.sessionUuid === sid;
      var current = recoveryResult ? !recoveryResult.silent : activeTurn === turn;
      if (activeTurn === turn) activeTurn = null;
      if (turn.sessionGone) {
        // The session is gone but Redux may still hold it (account switch): only release the stream state.
        if (sameSession) dispatch({ type: type.CHAT_STREAM_ENDED, payload: {} });
        return;
      }
      if (!current || !sameSession) return;
      dispatch({ type: type.CHAT_STREAM_ENDED, payload: {} });
      _persistMessages(getState().chatSession.messages);
    });
  };
}

// Resolves { endedCleanly } : true when the server sent a done or error event.
function readSSE(response, dispatch, getState, setAccum, getAccum, controller, restartTakingAWhileTimer, onEvent, currentAssistantUuid) {
  var reader = response.body.getReader();
  var decoder = new TextDecoder('utf-8');
  var streamTerminated = false;
  // currentAssistantUuid: mutable holder for the uuid of the assistant message currently streaming,
  // updated on each `chat.message_start` for role === 'assistant'.

  var parser = createSSEParser(function (evt) {
    if (onEvent) onEvent();
    handleEvent(evt, dispatch, getState, currentAssistantUuid, setAccum, getAccum, function () {
      streamTerminated = true;
      try { controller.abort(); } catch (e) { /* ignore */ }
    }, restartTakingAWhileTimer);
  });

  console.log('[SM-DEBUG] readSSE start');
  function pump() {
    if (streamTerminated) return Promise.resolve({ endedCleanly: true });
    return reader.read().then(function (chunk) {
      // [SM-DEBUG] raw stream chunk
      console.log('[SM-DEBUG] sse chunk', {
        done: chunk.done,
        bytes: chunk.value ? chunk.value.length : 0,
        terminated: streamTerminated,
      });
      if (chunk.done || streamTerminated) {
        parser.close();
        return { endedCleanly: streamTerminated };
      }
      var decoded = decoder.decode(chunk.value, { stream: true });
      console.log('[SM-DEBUG] sse raw', decoded.slice(0, 300));
      parser.feed(decoded);
      return pump();
    });
  }
  return pump();
}

function handleEvent(evt, dispatch, getState, currentAssistantUuid, setAccum, getAccum, terminate, restartTakingAWhileTimer) {
  // [SM-DEBUG] log every stream event except per-token deltas
  if (evt.event !== 'chat.delta') {
    console.log('[SM-DEBUG] SSE event', { event: evt.event, data: evt.data });
  }
  switch (evt.event) {
    case 'chat.message_start':
      if (evt.data.role === 'user') {
        dispatch({
          type: type.CHAT_USER_MESSAGE_UUID_ASSIGNED,
          payload: { uuid: evt.data.uuid },
        });
      } else if (evt.data.role === 'assistant') {
        currentAssistantUuid.value = evt.data.uuid;
        dispatch({
          type: type.CHAT_ASSISTANT_PLACEHOLDER_PUSHED,
          payload: { uuid: evt.data.uuid, createdAt: new Date().toISOString() },
        });
      }
      return;
    case 'chat.thinking':
      if (restartTakingAWhileTimer) restartTakingAWhileTimer();
      if (evt.data && typeof evt.data.label === 'string' && evt.data.label.length > 0) {
        dispatch({ type: type.CHAT_THINKING_LABEL_SET, payload: { label: evt.data.label } });
      }
      return;
    case 'chat.delta':
      if (getState().chatSession.thinkingLabel) dispatch({ type: type.CHAT_THINKING_LABEL_CLEARED });
      // Live deltas stream through the emitter, keyed by current assistant uuid.
      setAccum(getAccum() + evt.data.text);
      if (currentAssistantUuid.value) {
        emitStreaming({ type: 'delta', uuid: currentAssistantUuid.value, text: evt.data.text });
      }
      return;
    case 'chat.message_end':
      // Commit accumulated paragraph(s) to Redux for this assistant uuid.
      var endUuid = evt.data && evt.data.uuid ? evt.data.uuid : currentAssistantUuid.value;
      dispatch({
        type: type.CHAT_MESSAGE_APPEND_DELTA,
        payload: { text: getAccum(), uuid: endUuid },
      });
      setAccum('');
      if (endUuid) {
        emitStreaming({ type: 'paragraph_end', uuid: endUuid });
        emitStreaming({ type: 'stream_end', uuid: endUuid });
      }
      dispatch({ type: type.CHAT_MESSAGE_END, payload: { uuid: endUuid } });
      // Clear current assistant uuid; a fresh `chat.message_start` will set the next one.
      currentAssistantUuid.value = null;
      return;
    case 'tool.start':
      if (restartTakingAWhileTimer) restartTakingAWhileTimer();
      dispatch({
        type: type.CHAT_TOOL_START,
        payload: {
          toolUseId: evt.data.toolUseId,
          name: evt.data.name,
          label: evt.data.label,
        },
      });
      return;
    case 'tool.progress':
      if (restartTakingAWhileTimer) restartTakingAWhileTimer();
      dispatch({
        type: type.CHAT_TOOL_PROGRESS,
        payload: {
          toolUseId: evt.data.toolUseId,
          message: evt.data.message,
        },
      });
      return;
    case 'slate.updated':
      if (evt.data.source !== 'llm') return;
      console.log('[SlateMate SSE] slate.updated', evt.data);
      dispatch(showTranslationInPane(evt.data.translation));
      var statuses = getState().chatSession.toolStatuses;
      if (statuses && statuses.length > 0) {
        if (restartTakingAWhileTimer) restartTakingAWhileTimer();
        dispatch({ type: type.CHAT_TOOL_END, payload: { toolUseId: statuses[0].toolUseId } });
      }
      return;
    case 'keepalive':
      return;
    case 'error':
      // [SM-DEBUG] backend error event during stream
      console.log('[SM-DEBUG] SSE ERROR event', {
        code: evt.data && evt.data.code,
        message: evt.data && evt.data.message,
        data: evt.data,
      });
      dispatch({
        type: type.CHAT_MESSAGE_ERRORED,
        payload: { code: evt.data.code, message: evt.data.message },
      });
      terminate();
      return;
    case 'done':
      if (getState().chatSession.thinkingLabel) dispatch({ type: type.CHAT_THINKING_LABEL_CLEARED });
      terminate();
      return;
    default:
      return;
  }
}

export function retryLastMessage() {
  return function (dispatch, getState) {
    var messages = getState().chatSession.messages;
    if (getState().chatSession.streamStatus === 'streaming') return;
    for (var ai = messages.length - 1; ai >= 0; ai--) {
      if (messages[ai].role !== 'assistant') continue;
      // The server still holds the turn after still_working; a Retry would get 409 session_busy.
      if (messages[ai].errorCode === 'still_working') return;
      break;
    }
    // Find last user message and re-send. The errored/interrupted assistant
    // bubble stays in history; a fresh assistant turn is appended.
    // Attachments are NOT re-attached (server still has them).
    for (var i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        dispatch(sendMessage(messages[i].text, []));
        return;
      }
    }
  };
}
